<template id="gallery">
  <ion-page>
    <div class="gallery-controls">
      <ExtendableChip
        ref="deleteButton"
        v-if="allowDelete"
        class="gallery-delete-button"
        color="danger"
        :title="i18n.$t('forms.files.delete_confirmation')"
        v-model:extended="deleteConfirmationOpen"
        extendOnClick
        button
        extendToLeft
        @extendedClick="deleteMediaAtCurrentIndex()"
        :collapseTimeout="5000"
        >
        <template v-slot:permanent>
          <ion-icon :icon="trash" :alt="i18n.$t('forms.files.delete')"></ion-icon>
        </template>
      </ExtendableChip>
 
      <ion-button v-if="allowEdit && mediaCapabilitiesForCurrentIndex.allowEditThroughCamera" class="gallery-edit-button" @click="editMediaAtCurrentIndex()" color="success" fill="clear">
        <ion-icon slot="icon-only" :icon="pencil"></ion-icon>
      </ion-button>
      
      <ion-button class="gallery-download-button" @click="downloadMediaAtCurrentIndex()" color="primary" fill="clear">
        <ion-icon slot="icon-only" :icon="openOutline"></ion-icon>
      </ion-button>

      <ion-button class="gallery-close-button" @click="closeModal()" fill="clear">
        <ion-icon slot="icon-only" :icon="close"></ion-icon>
      </ion-button>
    </div>
    <div :class="['gallery-slides-container', slidesInitialized ? 'initialized' : undefined]">
      <swiper-container ref="slidesRef" class="gallery-slides" v-if="media"
        :centered-slides="true"
        :zoom-enabled="true"
        :zoom-max-ratio="5"
        :space-between="15"
        :keyboard-enabled="true"
        :initial-slide="selectedIndex"
        :init="false"
      > <!-- Swiper is initalized manually, when modal is ready -->
        <swiper-slide v-for="(media, mediaIndex) in media" :key="mediaIndex">
          <div class="swiper-zoom-container">
            <ZoomableMediaContainer
              class="swiper-zoom-target gallery-swiper-zoom-target"
              :media="media"
              :imposedOpacity="getImposedImageOpacityForIndex(mediaIndex) / 100"
              :currentPage="getCurrentPageForIndex(mediaIndex)"
              @update:mediaCapabilities="(newCapabilities) => setMediaCapabilities(mediaIndex, newCapabilities)"
              :zoomValue="zoomValue">
            </ZoomableMediaContainer>
          </div>
        </swiper-slide>
      </swiper-container>
    </div>
    <div class="gallery-controls-bottom">
      <div>
        <ion-button @click="zoomOut()" fill="clear" class="gallery-zoom-button" :disabled="!zoomPossible.out">
          <font-awesome-icon slot="icon-only" :icon="faMagnifyingGlassMinus" />
        </ion-button>
        <ion-button @click="zoomIn()" fill="clear" class="gallery-zoom-button"  :disabled="!zoomPossible.in">
          <font-awesome-icon slot="icon-only" :icon="faMagnifyingGlassPlus" />
        </ion-button>
      </div>

      <div v-if="imposedImageOpacity != null" class="gallery-opacity-selection">
        <ion-button @click="decreaseOpacity()" class="gallery-contrast-decrease" fill="clear" slot="start" :disabled="imposedImageOpacity <= 0">
          <ion-icon slot="icon-only" :icon="contrast"></ion-icon>
        </ion-button>
        <ion-range min="0" max="100" snaps="false" :value="imposedImageOpacity" @ionChange="(event) => imposedImageOpacity = event.target.value">
        </ion-range>
        <ion-button @click="increaseOpacity()" class="gallery-contrast-increase" fill="clear" slot="end" :disabled="imposedImageOpacity >= 100">
          <ion-icon slot="icon-only" :icon="contrast"></ion-icon>
        </ion-button>
      </div>

      <div v-if="mediaCapabilitiesForCurrentIndex.hasPages" class="gallery-page-selection">
        <div class="gallery-page-selection-input-container">
          <ion-icon slot="icon-only" :icon="documents"></ion-icon>
          <ion-input ref="galleryPageSelectionInput" class="gallery-page-selection-input" min="1" :max="mediaCapabilitiesForCurrentIndex.pages" :value="1" @ionBlur="(event) => event.target.value = mediaCurrentPage" @ionChange="(event) => mediaCurrentPage = event.target.value"></ion-input>
        </div>
        <span class="gallery-page-max-page">
          /&nbsp;{{ mediaCapabilitiesForCurrentIndex.pages }}
        </span>
        <ion-button @click="decreasePage()" class="gallery-page-decrease" fill="clear" :disabled="mediaCurrentPage <= 1">
          <ion-icon slot="icon-only" :icon="chevronUp"></ion-icon>
        </ion-button>
        <ion-button @click="increasePage()" class="gallery-page-increase" fill="clear" :disabled="mediaCurrentPage >= mediaCapabilitiesForCurrentIndex.pages">
          <ion-icon slot="icon-only" :icon="chevronDown"></ion-icon>
        </ion-button>
      </div>
    
      <div>
        <ion-button @click="goToPrevious()" class="gallery-index-button" fill="clear" :disabled="selectedMediaIndex <= 0">
          <ion-icon slot="icon-only" :icon="chevronBack"></ion-icon>
        </ion-button>
        <span 
          class="page-index"
          :style="(media != null && media[selectedMediaIndex] != null && media[selectedMediaIndex].color != null) ? `color: var(${media[selectedMediaIndex].color}, var(--anirec-color-blue-50))` : undefined">
          {{ selectedMediaIndex + 1 }} / {{ mediaCount }}
          </span>
        <ion-button @click="goToNext()" class="gallery-index-button" fill="clear" :disabled="(selectedMediaIndex + 1) >= mediaCount">
          <ion-icon slot="icon-only" :icon="chevronForward"></ion-icon>
        </ion-button>
      </div>
    </div>
  </ion-page> 
</template>

<script>
import { IonPage, IonButton, IonIcon, IonRange, IonInput, modalController, getPlatforms } from '@ionic/vue';
import { computed, defineComponent, onMounted, ref } from 'vue';

import { useStore } from 'vuex';

import ExtendableChip from '@/components/ExtendableChip.vue';
import ZoomableMediaContainer from '@/components/ZoomableMediaContainer.vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMagnifyingGlassPlus, faMagnifyingGlassMinus } from '@fortawesome/free-solid-svg-icons';

import { close, trash, chevronBack, chevronForward, pencil, contrast, chevronDown, chevronUp, documents, openOutline } from 'ionicons/icons';

import { useI18n } from "@/utils/i18n";
import { download, mimeToExtension, sanitizeFileOrFolderName } from '@/utils/file_download';

import { createCollapseAnimationToPoint, createExtendAnimationFromPoint, slowModalFadeOutAnimation } from '@/utils/animations';

import _ from 'lodash';

const MODAL_ID = 'gallery-modal-instance';

const Gallery = defineComponent({
  name: 'Gallery',
  components: { IonPage, IonButton, IonIcon, IonRange, IonInput, FontAwesomeIcon, ExtendableChip, ZoomableMediaContainer },
  props: {
    'modalReadyPromise': Promise,
    'media': Array, //Expects array of objects with type, mime and blobURL set, color is an optional value that can be set to indicate media status
    'selectedIndex': {
      type: Number,
      default: 0
    },
    'allowDelete': {
      type: Boolean,
      default: false
    },
    'openDeleteConfirmation': {
      type: Boolean,
      default: false
    },
    'deleteCallback': Function,
    'allowEdit': {
      type: Boolean,
      default: false
    },
    'editCallback': Function,
    'closeCallback': Function
  },
  setup(props) {
    const store = useStore();

    const i18n = useI18n();

    const deleteButton = ref(null);

    const galleryPageSelectionInput = ref(null);

    const slidesRef = ref(null);

    //Open it initially according to the prop and then set it internally to the current state
    const deleteConfirmationOpen = ref(false);

    const zoomValue = ref(1);

    const selectedMediaIndex = ref(props.selectedIndex);

    //Internal list for keeping track of deleted media - Not used for generating slides!
    const currentMedia = ref(_.cloneDeep(props.media));

    const mediaCount = computed(() => currentMedia.value.length);

    const zoomPossible = computed(() => {
      return {
        out: zoomValue.value > 1,
        in: zoomValue.value < maxZoomRatio.value
      };
    });

    const maxZoomRatio = ref(5);

    const mediaCapabilities = ref({});

    const setMediaCapabilities = function(index, newCapabilities) {
      if (newCapabilities == null) mediaCapabilities.value[index] = {};
      else mediaCapabilities.value[index] = newCapabilities;
    }

    const mediaCapabilitiesForCurrentIndex = computed(() =>{
      return mediaCapabilities.value[selectedMediaIndex.value] || {};
    });

    //Saves any changed opacity from the default value
    const userSetOpacity = ref({});
    const DEFAULT_OPACITY = 60;

    const getImposedImageOpacityForIndex = computed(() => {
      return function(mediaIndex) {
        if (mediaIndex != null) {
          if (mediaIndex in userSetOpacity.value) return userSetOpacity.value[mediaIndex];
          else if (currentMedia.value[mediaIndex] != null && currentMedia.value[mediaIndex].imposedImage != null) return DEFAULT_OPACITY;
        }
        return undefined;
      }
    });

    const imposedImageOpacity = computed({
      get: function() {
        return getImposedImageOpacityForIndex.value(selectedMediaIndex.value);
      },
      set: function(newOpacity) {
        let opacityValue = newOpacity;
        if (opacityValue < 0) opacityValue = 0;
        if (opacityValue > 100) opacityValue = 100;
        if (selectedMediaIndex.value != null) userSetOpacity.value[selectedMediaIndex.value] = opacityValue;
      }
    });

    const OPACITY_STEP = 10;

    const increaseOpacity = function() {
      imposedImageOpacity.value = imposedImageOpacity.value + OPACITY_STEP;
    }

    const decreaseOpacity = function() {
      imposedImageOpacity.value = imposedImageOpacity.value - OPACITY_STEP;
    }

    //Save any changes to the current page for each index
    const userSetCurrentPage = ref({});

    const getCurrentPageForIndex = computed(() => {
      return function(mediaIndex) {
        if (mediaIndex != null) {
          if (mediaIndex in userSetCurrentPage.value) return userSetCurrentPage.value[mediaIndex] || 1;
        }
        return 1;
      }
    });

    const mediaCurrentPage = computed({
      get: function() {
        return getCurrentPageForIndex.value(selectedMediaIndex.value);
      },
      set: function(newPage) {
        let pageValue = parseInt(newPage);
        if (isNaN(pageValue) || pageValue == null) {
          pageValue = null;
        } else {
          let maxPages = _.get(mediaCapabilitiesForCurrentIndex.value, ['pages'], 1);
          if (pageValue < 1) pageValue = 1;
          if (pageValue > maxPages) pageValue = maxPages;
        }
        if (selectedMediaIndex.value != null) userSetCurrentPage.value[selectedMediaIndex.value] = pageValue;
      }
    });

    const increasePage = function() {
      mediaCurrentPage.value = mediaCurrentPage.value + 1;
      //Update only when changed not through input to not mess with the user entering a value
      if (galleryPageSelectionInput.value != null) galleryPageSelectionInput.value.$el.value = mediaCurrentPage.value;
    }

    const decreasePage = function() {
      mediaCurrentPage.value = mediaCurrentPage.value - 1;
      //Update only when changed not through input to not mess with the user entering a value
      if (galleryPageSelectionInput.value != null) galleryPageSelectionInput.value.$el.value = mediaCurrentPage.value;
    }

    const closeModal = function(){
      if (props.closeCallback != null) props.closeCallback(selectedMediaIndex.value);
      modalController.dismiss(undefined, undefined, MODAL_ID);
    }

    const zoomOut = async function(){
      try {
        let swiper = slidesRef.value.swiper;
        if (swiper && swiper.zoom) {
          swiper.zoom.in((zoomValue.value || 1) - 0.5);
        }
      } catch {
        return;
      }
    }

    const zoomIn = async function(){
      try {
        let swiper = slidesRef.value.swiper;
        if (swiper && swiper.zoom) {
          swiper.zoom.in((zoomValue.value || 1) + 0.5);
        }
      } catch {
        return;
      }
    }

    const goToPrevious = async function(){
      try {
        let swiper = slidesRef.value.swiper;
        if (swiper) {
          swiper.slidePrev();
        }
      } catch {
        return;
      }
    }

    const goToNext = async function(){
      try {
        let swiper = slidesRef.value.swiper;
        if (swiper) {
          swiper.slideNext();
        }
      } catch {
        return;
      }
    }

    const deleteMediaAtCurrentIndex = async function() {
      if (props.deleteCallback != null) {
        try {
          let swiper = slidesRef.value.swiper;
          if (swiper && (swiper.activeIndex != null)) {
            props.deleteCallback(swiper.activeIndex, currentMedia.value[swiper.activeIndex]);
            //If just one is left, we close the modal
            if(mediaCount.value == 1) {
              closeModal();
            } 
            //Otherwise the slide is removed from the swiper and from the internal list. index in swiper is never sparse, even after removal!
            else {
              _.pullAt(currentMedia.value, swiper.activeIndex);
              swiper.removeSlide(swiper.activeIndex);
              //Close confirmation after deletion for next media to have all confirmation steps again!
              deleteConfirmationOpen.value = false;
            }
          }
        } catch {
          return;
        }
      }
    }

    const editMediaAtCurrentIndex = async function() {
      if (props.editCallback != null) {
        try {
          let swiper = slidesRef.value.swiper;
          if (swiper && (swiper.activeIndex != null)) {
            //Modify animation to slowly fade away - Better transition to editing
            let modal = await modalController.getTop();
            if (modal != null) modal.leaveAnimation = slowModalFadeOutAnimation;
            //Start editing
            props.editCallback(swiper.activeIndex, currentMedia.value[swiper.activeIndex]);
            //Close the modal, because we modify the media list potentially
            closeModal();
          }
        } catch {
          return;
        }
      }
    }

    const loadedThroughRelay = computed(() => store.getters['auth/isAuthenticationRelayActive']);

    const downloadMediaAtCurrentIndex = async function() {
      try {
        let swiper = slidesRef.value.swiper;
        if (swiper && (swiper.activeIndex != null)) {
          let mediaForDownload = currentMedia.value[swiper.activeIndex];
          if (mediaForDownload != null) { //TODO Maybe change behaviour with imposed images at some point
            //If imposedImage is valid, load this instead
            if (mediaForDownload.imposedImage != null) {
              mediaForDownload = mediaForDownload.imposedImage;
            }

            let fetchPromise;
            if (!(loadedThroughRelay.value)) { //Load it manually - no relay available
              fetchPromise = store.dispatch('fetchMediaWithAuthentication', {mediaURL: mediaForDownload.blobURL, returnBlob: true});
            } else {
              fetchPromise = fetch(mediaForDownload.blobURL, {
                method: 'GET',
                headers: {
                  'Accept': mediaForDownload.mime
                }
              }).then((fetchResult) => fetchResult.blob());
            }
            
            fetchPromise.then((blob) => {
              let usedFilename = mediaForDownload.filename || mediaForDownload.name || mediaForDownload.hash || 'file';
              let usedExtension = ((mediaForDownload.ext != null && mediaForDownload.ext.length) ? mediaForDownload.ext : mediaForDownload.extension) || `.${mimeToExtension(mediaForDownload.mime)}` || '';
              //If the extension is already the same, remove it
              if (_.endsWith(usedFilename, usedExtension)) {
                usedExtension = '';
              }

              return download(i18n, sanitizeFileOrFolderName(`${usedFilename}${usedExtension}`, '_'), blob, blob.type, getPlatforms(), 'attachments');
            })
            .catch((error) => {
              console.error(error);
              return; //TODO No error handling?
            })
          }
        }
      } catch {
        return;
      }
    }

    //Signals readiness of the slides to be displayed
    const slidesInitialized = ref(false);

    const initSwiper = function(){
      try {
        let swiperEl = slidesRef.value;
        if (swiperEl) {
          swiperEl.initialize();
          let swiper = swiperEl.swiper;
          if (swiper) {
            swiper.on('zoomChange', (eventSwiper, scale) => {
              zoomValue.value = scale
            });
            //Listen for delete keys and interact with delete button
            swiper.on('keyPress', (eventSwiper, keyCode) => {
              if (keyCode === 8 /* Backspace */ || keyCode === 46 /* Delete */) deleteButton.value.$el.click();
            });
            swiper.on('activeIndexChange', function () {
              selectedMediaIndex.value = this.activeIndex;
              //Close confirmation when changing media
              deleteConfirmationOpen.value = false;
              //Pause all videos in the gallery when the current active slide changes
              document.querySelectorAll('.gallery-slides-container video').forEach(videoElement => videoElement.pause());
            });
          }
        }
      } catch {
        //Do nothing on error to finish init
      }
      slidesInitialized.value = true;
      setTimeout(() => deleteConfirmationOpen.value = props.openDeleteConfirmation, 100); //Set from props after initialization to keep in initial state. Timeout is used to open in when viewed.
    }

    onMounted(() => {
      //Initialize swiper manually!
      //Workaround to fix issue, where the interaction does not work properly when buttons are added. If we wait for readiness of the modal, the initialization can happen properly
      if (props.modalReadyPromise != null) {
        props.modalReadyPromise.then(() => initSwiper());
      } else {
        initSwiper();
      }
    });

    return {
      i18n,
      deleteButton,
      galleryPageSelectionInput,
      slidesRef,
      mediaCapabilities,
      mediaCapabilitiesForCurrentIndex,
      setMediaCapabilities,
      zoomPossible,
      selectedMediaIndex,
      mediaCount,
      slidesInitialized,
      deleteConfirmationOpen,
      closeModal,
      zoomValue,
      zoomOut,
      zoomIn,
      goToPrevious,
      goToNext,
      deleteMediaAtCurrentIndex,
      editMediaAtCurrentIndex,
      downloadMediaAtCurrentIndex,
      imposedImageOpacity,
      getImposedImageOpacityForIndex,
      increaseOpacity,
      decreaseOpacity,
      getCurrentPageForIndex,
      mediaCurrentPage,
      increasePage,
      decreasePage,
      close,
      faMagnifyingGlassPlus,
      faMagnifyingGlassMinus,
      trash,
      chevronBack,
      chevronForward,
      pencil,
      contrast,
      chevronDown,
      chevronUp,
      documents,
      openOutline
    }
  }
});

export async function openGallery(component, media, selectedIndex, additionalProps, offset = {x: undefined, y: undefined}, animationInitialHeight){
  //Create a promise to signal the component when the modal is ready to initialize view elements
  let modalReady;
  const modalReadyPromise = new Promise((resolve) => modalReady = resolve);

  if (component != null && media != null) {
    const modal = await modalController
      .create({
        enterAnimation: createExtendAnimationFromPoint(offset, animationInitialHeight),
        leaveAnimation: createCollapseAnimationToPoint(offset, animationInitialHeight),
        component,
        id: MODAL_ID,
        cssClass: 'media-gallery',
        componentProps: {
          modalReadyPromise,
          media,
          selectedIndex,
          ...additionalProps
        },
      });
    const presentPromise = modal.present();
    presentPromise.then(() => modalReady());

    return presentPromise;
  }
}

export default Gallery;
</script>

<style>
.media-gallery {
  --background: transparent;
  --height: 100%;
  --width: 100vw;
  --min-height: 0px;
  --max-height: 100%;
  --max-width: 100vw;
  --backdrop-opacity: var(--ion-backdrop-opacity);
  --ion-safe-area-top: inherit;
  --ion-safe-area-bottom: inherit;
  --ion-safe-area-left: inherit;
  --ion-safe-area-right: inherit;
}

.media-gallery .modal-wrapper {
    border-radius: 0px!important;
}

.media-gallery .modal-wrapper > .ion-page {
  margin-top: calc(var(--ion-safe-area-top) + 1vh);
  margin-bottom: calc(var(--ion-safe-area-bottom) + 1vh);
  margin-left: var(--ion-safe-area-left);
  margin-right: var(--ion-safe-area-right);
}

.gallery-slides {
  --max-slide-height-fixed: 70vh;
  --max-slide-height: calc(var(--max-slide-height-fixed, 70vh) - var(--ion-safe-area-top) - var(--ion-safe-area-bottom));;
  height: var(--max-slide-height, 70vh);
  margin: 0px;
}

.gallery-controls {
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  display: flex;
}

.gallery-controls-bottom {
  width: 100%;
  justify-content: space-between;
  align-items: center;
  display: flex;
  padding-left: 10px;
}

.gallery-controls-bottom > div {
  align-items: center;
  display: flex;
}

.gallery-controls ion-button, .gallery-controls-bottom * {
  --color: var(--anirec-color-blue-50);
  color: var(--color);
  --ripple-color: var(--anirec-color-blue-50);
}

.gallery-swiper-zoom-target {
  max-height: var(--max-slide-height, 70vh);
  max-width: 100vw;
}

.gallery-delete-button {
  --custom-size: 2.25em!important;
  margin-inline-start: clamp(5px, 2%, 30px);
}

.gallery-close-button {
  color: var(--anirec-color-blue-50);
  margin-inline-start: clamp(5px, 2%, 30px);
}

.gallery-opacity-selection {
  display: flex;
  flex-grow: 1;
  justify-content: center;
  margin-inline: 10px;
}

.gallery-opacity-selection ion-range {
  max-width: 400px;
  padding: 0px;
}

@media (max-width: 500px) {
  .gallery-opacity-selection ion-range {
    display: none!important;
  }

  .gallery-page-max-page {
    display: none!important;
  }
}

.gallery-page-selection {
  display: flex;
  justify-content: center;
  align-items: center;
}

.gallery-page-selection ion-button, .gallery-index-button {
  --padding-start: 5px;
  --padding-end: 5px;
}

.gallery-zoom-button, .gallery-opacity-selection ion-button {
  --padding-start: 10px;
  --padding-end: 10px;
}

.gallery-page-selection-input-container {
  display: flex;
  flex-direction: row;
  border: 1px solid currentColor;
  border-radius: 5px;
  align-items: center;
  margin-inline: 5px;
  padding-inline: 5px;
}

.gallery-page-selection-input {
  max-width: 3ch;
  min-width: 3ch;
  --padding-top: 5px;
  --padding-bottom: 5px;
  margin-inline: 0px;
}

.gallery-page-max-page {
  margin-right: 10px;
}

.gallery-contrast-decrease ion-icon, .gallery-contrast-increase ion-icon {
  position: relative;
  font-size: 20px;
  margin-inline: 5px;
}

.gallery-contrast-decrease ion-icon::after, .gallery-contrast-increase ion-icon::after {
  content: "-";
  color: var(--anirec-color-blue-50);
  position: absolute;
  top: 50%;
  right: 0%;
  font-size: 0.6em;
  vertical-align: middle;
  mix-blend-mode: difference;
}

.gallery-contrast-decrease, .gallery-contrast-increase {
  --padding-start: 10px;
  --padding-end: 10px;
}

.gallery-contrast-decrease ion-icon::after {
  content: "-";
  transform: translate(-0.25em, -54%);
}

.gallery-contrast-increase ion-icon::after {
  content: "+";
  transform: translate(-0.2em, -54%);
}

/* Hide gallery until fully initialized to prevent pop-in. Use transition for smoothness */
.gallery-slides-container {
  opacity: 0;
  transition: opacity 0.15s ease-out;
}
.gallery-slides-container.initialized {
  opacity: 1;
}

.page-index {
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  white-space: nowrap;
  color: var(--anirec-color-blue-50);
}
</style>
